import { DrawerBase } from 'bog-arcade-core-ui/core/DrawerBase';
import { TimelineLite } from 'gsap';
import { GameLayers } from '../../../core/enums';
import { resourceHelper } from '../../../core/resourceHelper';
import { sharedSourceNames } from '../../../core/sourceNames';
import { translationKeys } from '../../../core/translation/translation';
import {
  bonusOddStyle,
  freeSpinTitleStyle,
  freeSpinValueStyle,
  multiplierTitleStyle,
  multiplierValueStyle,
} from '../../../core/textStyles';
import {
  BONUS_SPIN_RESPONSE,
  BONUS_TOTAL_ODD,
  MULTIPLIER_BLINKING_STATE,
  MULTIPLIER_STATE,
} from '../../../stateManagment';
import { getAmount } from '../../../core/helpers/getAmount';
import { delay } from 'bog-arcade-core-ui/helpers';
import { animationDrawer } from '../../../core/AnimationDrawer';
import { soundService } from '../../../services/helperServices/SoundService';

export class BaseBonusBoardDrawer extends DrawerBase {
  setupLayers(layers) {
    this.container = layers[GameLayers.bonusBoard];
  }

  getMultiplier({ scale = 1 }) {
    const container = this.createContainer('multiplier');
    const imageContainer = this.createSprite(resourceHelper.get(sharedSourceNames.multiplier));
    const titleText = this.createText(translationKeys.multiplier, multiplierTitleStyle);
    const valueText = this.createText(`X0`, multiplierValueStyle);

    titleText.anchor.set(.5);
    titleText.scale.set(scale);
    valueText.anchor.set(.5);
    valueText.scale.set(scale);

    imageContainer.scale.set(scale);
    imageContainer.anchor.set(.5, 1);

    this.setPosition(valueText, { y: -246 * scale });
    this.setPosition(titleText, { y: -132 * scale });

    this.addSubscriber(MULTIPLIER_BLINKING_STATE.subscribe(value => {
      if (value) {
        animationDrawer.blinking(valueText, .2, [scale + 1, .5, scale]);
      }
    }));

    container.addChild(
      imageContainer,
      titleText,
      valueText,
    );
    return {
      content: container,
      onSetValue: (value, { prev }) => {
        if (prev) {
          animationDrawer.blinking(valueText, .2, [scale + 1, .5, scale]);
        }
        valueText.text = `X${value}`;
      },
      * [Symbol.iterator]() {
        yield this.content;
        yield this.onSetValue;
      },
    };
  }

  getFreeSpin({ scale = 1 }) {
    const container = this.createContainer('freeSpin');
    const imageContainer = this.createSprite(resourceHelper.get(sharedSourceNames.freeSpin));
    const titleText = this.createText(translationKeys.freeSpins, freeSpinTitleStyle);
    const valueText = this.createText(0, freeSpinValueStyle);

    titleText.anchor.set(.5);
    titleText.scale.set(scale);
    valueText.anchor.set(.5);
    valueText.scale.set(scale);

    imageContainer.scale.set(scale);
    imageContainer.anchor.set(.5, 1);

    this.setPosition(valueText, { y: -246 * scale });
    this.setPosition(titleText, { y: -132 * scale });
    container.addChild(
      imageContainer,
      titleText,
      valueText,
    );
    return {
      content: container,
      onSetValue: (value, { prev }) => {
        if (prev) {
          animationDrawer.blinking(valueText, .2, [scale + 1, .5, scale]);
        }
        valueText.text = value;
      },
      * [Symbol.iterator]() {
        yield this.content;
        yield this.onSetValue;
      },
    };
  }

  getTotalWin({ scale = 1, containerScale = 1.2, fontSize = 45, multiplierPosition = { x: 760, y: 580 } }) {
    const container = this.createContainer('totalWin');
    const multiplier = this.createText(`X0`, multiplierValueStyle);
    const imageContainer = this.createSprite(resourceHelper.get(sharedSourceNames.bonusLeaf));
    const titleText = this.createText(
      `ODD:`,
      {
        ...bonusOddStyle,
        fontSize: fontSize,
      },
    );

    const valueText = this.createText(
      BONUS_TOTAL_ODD.getValue() ? 'x' + BONUS_TOTAL_ODD.getValue() : '',
      {
        ...bonusOddStyle,
        fontSize: fontSize,
      },
    );

    this.setPosition(multiplier, multiplierPosition);

    imageContainer.scale.set(containerScale);
    valueText.scale.set(scale);
    titleText.scale.set(scale);
    imageContainer.anchor.set(.5);
    valueText.anchor.set(.5);
    titleText.anchor.set(.5);
    multiplier.anchor.set(.5);
    multiplier.visible = false;

    this.setPosition(titleText, { y: -25 });
    this.setPosition(valueText, { y: 20 });

    container.addChild(
      multiplier,
      imageContainer,
      titleText,
      valueText,
    );
    return {
      content: container,
      onSetOdd: (value, { prev }) => {
        if (prev !== null) {
          titleText.text = `ODD:`;
          valueText.text = value ? 'x' + value : '';
          new TimelineLite()
            .to(valueText, { duration: .2, pixi: { scale: scale + .2 } })
            .to(valueText, { duration: .2, pixi: { scale: scale } });
        }
      },
      onSetWin: (value, { prev }) => {
        if (prev !== null) {
          new TimelineLite()
            .to(valueText,
              {
                delay: .4,
                duration: .2,
                pixi: { scale: 0 },
                onComplete: () => {
                  const { nativeWinAmount } = BONUS_SPIN_RESPONSE.getValue();
                  titleText.text = `WIN:`;
                  valueText.text = getAmount(nativeWinAmount);
                },
              })
            .to(valueText, {
              duration: .2,
              pixi: { scale },
              onComplete: () => {
                MULTIPLIER_BLINKING_STATE.next(true);
                const multiplierData = MULTIPLIER_STATE.getValue();
                multiplier.text = `X ${multiplierData}`;
                multiplier.visible = !!multiplierData;
                multiplier.scale.set(2);
                this.setPosition(multiplier, multiplierPosition);
                multiplier.alpha = 1;
                multiplier.visible = true;
                soundService.playMultiplierSound();
                new TimelineLite().to(multiplier, {
                  duration: .6, x: 0, y: 0,
                  pixi: { scale: .5 },
                  onComplete: () => {
                    multiplier.visible = false;
                  },
                });
              },
            })
            .to(valueText, {
              delay: .2,
              onComplete: () => {
                titleText.text = 'WIN:';
                valueText.text = getAmount(value);
                multiplier.visible = true;
                animationDrawer.blinking(valueText, .1, [.5, scale]);
                delay(() => {
                  BONUS_TOTAL_ODD.next(0);
                  MULTIPLIER_BLINKING_STATE.next(false);
                }, 2000);
              },
            });
        }
      },
      * [Symbol.iterator]() {
        yield this.content;
        yield this.onSetOdd;
        yield this.onSetWin;
      },
    };
  }
}